// @flow
import 'fetch-everywhere'
import omitEmptyFields from 'mz-utils/omitEmptyFields'
import APIError from './APIError'
import is from 'mz-utils/is'

// Types
type Method = 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT'

export type RequestOptions = {
  query?: Object,
  method?: Method,
  body?:
    | string
    | URLSearchParams
    | FormData
    | Blob
    | ArrayBuffer
    | DataView
    | Object,
  headers?: Object,
  credentials?: 'omit' | 'same-origin' | 'include',
}

type ErrorData = {
  user_message?: string,
  message?: string,
  detail?: string,
  code: number | string,
}

// Constants
const DEFAULT_ERROR_MESSAGE_TEXT =
  'Our API returned an unexpected error, please contact ' +
  'support to resolve the problem'

// Utils
function isFormData(value): boolean {
  return value instanceof FormData
}

function getBodyValue({ body }) {
  if (!body) {
    return null
  }
  if (isFormData(body)) {
    return body
  }
  return JSON.stringify(body)
}

export const handleResponse = (
  url: string,
  method: Method,
  options?: Object
) => {
  return ([content, response]: [any, Object]) => {
    if (!response.ok) {
      const errorData: ErrorData =
        content && is.array(content) && content.length
          ? content[0]
          : content || {}

      const errorMessage =
        errorData.user_message ||
        errorData.message ||
        errorData.detail ||
        DEFAULT_ERROR_MESSAGE_TEXT

      const error = new APIError(
        errorMessage,
        errorData.code || 0,
        response.status,
        { url, method, options },
        content
      )

      throw error
    }
    return content
  }
}

/**
 * Performs an HTTP Request using `fetch` and process response
 * to return JSON value. It also log any error returned by the API
 * with our centralized global errors logger
 * @param  {string} method Name of the method (GET, POST, etc...)
 * @param  {string} url URL to call
 * @param  {Object} params Request options (http://github.github.io/fetch/#options)
 * @return {Promise|Error} A resolved Promise with the server response or Rejected with an Error
 * @private
 */
function makeRequest(
  method: Method,
  url: string,
  params?: RequestOptions = {}
): Promise<any> {
  const options = omitEmptyFields({
    ...params,
    method,
    body: getBodyValue(params),
  })

  // Remove the 'Distinct-Id' for .json files. Distinct-Id is added for posthog.
  if (url.includes('.json')) {
    delete options.headers['Distinct-Id']
  }

  return fetch(url, options)
    .then((response) => {
      const contentType = response.headers
        ? response.headers.get('content-type') || ''
        : ''
      let content

      if (response.status === 204) {
        content = null
      } else if (contentType.indexOf('json') >= 0) {
        content = response.json()
      } else {
        content = response.text()
      }

      return Promise.all([content, response])
    })
    .then(handleResponse(url, method, options))
}

export default {
  /**
   * Performs a HTTP GET Request
   * @param  {string} url URL to call
   * @param  {RequestOptions} options Request options (http://github.github.io/fetch/#options)
   * @return {Promise|Error} A resolved Promise with the server response or Rejected with an Error
   */
  get(url: string, options?: RequestOptions): Promise<any> {
    return makeRequest('GET', url, options)
  },

  /**
   * Performs a HTTP POST Request
   * @param  {string} url URL to call
   * @param  {RequestOptions} options Request options (http://github.github.io/fetch/#options)
   * @return {Promise|Error} A resolved Promise with the server response or Rejected with an Error
   */
  post(url: string, options?: RequestOptions): Promise<any> {
    return makeRequest('POST', url, options)
  },

  /**
   * Performs a HTTP PUT Request
   * @param  {string} url URL to call
   * @param  {RequestOptions} options Request options (http://github.github.io/fetch/#options)
   * @return {Promise|Error} A resolved Promise with the server response or Rejected with an Error
   */
  put(url: string, options?: RequestOptions): Promise<any> {
    return makeRequest('PUT', url, options)
  },

  /**
   * Performs a HTTP PATCH Request
   * @param  {string} url URL to call
   * @param  {RequestOptions} options Request options (http://github.github.io/fetch/#options)
   * @return {Promise|Error} A resolved Promise with the server response or Rejected with an Error
   */
  patch(url: string, options?: RequestOptions): Promise<any> {
    return makeRequest('PATCH', url, options)
  },

  /**
   * Performs a HTTP DELETE Request
   * @param  {string} url URL to call
   * @param  {RequestOptions} options Request options (http://github.github.io/fetch/#options)
   * @return {Promise|Error} A resolved Promise with the server response or Rejected with an Error
   */
  delete(url: string, options?: RequestOptions): Promise<any> {
    return makeRequest('DELETE', url, options)
  },
}
